// The Corpus browse surface — your ingested sessions, opened. Where Adherence
// shows the aggregate, this lists the underlying sessions and opens each one's
// FULL raw transcript (code, prompts, tool I/O) — the opt-in capture made
// reachable, so insight can come from the real content, not just the signals.
//
// Reads `GET /ingest/sessions` for the index; a row expands to fetch its raw
// (`GET …/raw`, inflated by the browser) and shows it, with a download. Calm by
// default: the transcript is the one heavy thing, loaded only when you open it.

import { useEffect, useState } from "react";
import { getIngestedSessions, getRawSession } from "../../api/client";
import type { IngestedSessionSummary } from "@waytide/core";
import { shortModel } from "@waytide/core";
import "./corpus.css";

/** Compact magnitude: 324647 → "324k", 1499 → "1.5k", 880 → "880". */
function fmtCount(n: number): string {
  if (n >= 10000) return `${Math.round(n / 1000)}k`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return `${n}`;
}

/** `started_at` is micros since epoch (the ingest schema). Render a plain date;
 * fall back gracefully for missing/odd values. */
function fmtWhen(micros: number): string {
  if (!micros) return "—";
  const ms = micros > 1e14 ? micros / 1000 : micros;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function CorpusView() {
  const [sessions, setSessions] = useState<IngestedSessionSummary[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getIngestedSessions()
      .then((r) => alive && setSessions(r.sessions))
      .catch(
        (e) => alive && setError(e instanceof Error ? e.message : String(e)),
      );
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="wt-dash-pane wt-corpus">
      <header className="wt-dash-pane__head">
        <h1 className="wt-dash-pane__title">Corpus</h1>
        <p className="wt-dash-pane__sub">
          Your ingested sessions in full — code, prompts, tool calls. Open one to
          read its raw transcript or download it. Opt-in; it's all yours.
        </p>
      </header>

      {!sessions && !error && (
        <p className="wt-dash-empty">Reading your corpus…</p>
      )}
      {error && (
        <p className="wt-dash-empty">Couldn't reach your corpus — {error}</p>
      )}
      {sessions && sessions.length === 0 && (
        <div className="wt-corpus-empty">
          <p>No sessions ingested yet.</p>
          <p className="wt-corpus-empty__how">
            On a machine with your agent history, run{" "}
            <code>waytide-runner scan</code> to upload it — the full transcripts,
            opt-in. Then they appear here.
          </p>
        </div>
      )}
      {sessions && sessions.length > 0 && (
        <ul className="wt-corpus-list" data-testid="corpus-list">
          {sessions.map((s) => (
            <CorpusRow key={s.source_session_id} session={s} />
          ))}
        </ul>
      )}
    </div>
  );
}

/** Cap the inline render so a marathon transcript can't choke the DOM — the
 * full thing is always one click away via Download. */
const PREVIEW_CHARS = 200_000;

function CorpusRow({ session: s }: { session: IngestedSessionSummary }) {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const title = [s.model ? shortModel(s.model) : s.harness, s.project || s.device]
    .filter(Boolean)
    .join(" · ");

  async function toggle() {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (raw === null && !loading) {
      setLoading(true);
      setErr(null);
      try {
        setRaw(await getRawSession(s.source_session_id));
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    }
  }

  function download() {
    if (raw === null) return;
    const blob = new Blob([raw], { type: "application/x-ndjson" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${s.source_session_id}.jsonl`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const truncated = raw !== null && raw.length > PREVIEW_CHARS;

  return (
    <li className="wt-corpus-row">
      <button
        type="button"
        className="wt-corpus-row__head"
        onClick={() => void toggle()}
        aria-expanded={open}
      >
        <span className="wt-corpus-row__title">{title}</span>
        <span className="wt-corpus-row__meta">
          {fmtWhen(s.started_at)} · {plural(s.message_count, "msg")} · peak{" "}
          {fmtCount(s.peak_context_tokens)} ctx
          {s.tool_error_count > 0
            ? ` · ${plural(s.tool_error_count, "tool-err")}`
            : ""}
        </span>
        <span className="wt-corpus-row__cue" aria-hidden="true">
          {open ? "Hide" : "Open"}
        </span>
      </button>
      {open && (
        <div className="wt-corpus-row__body">
          {loading && (
            <p className="wt-corpus-row__status">Reading the transcript…</p>
          )}
          {err && (
            <p className="wt-corpus-row__status">Couldn't open it — {err}</p>
          )}
          {raw !== null && !loading && (
            <>
              <div className="wt-corpus-row__actions">
                <span className="wt-corpus-row__size">
                  {fmtCount(raw.length)} chars
                  {truncated ? " · preview" : ""}
                </span>
                <button
                  type="button"
                  className="wt-corpus-row__dl"
                  onClick={download}
                >
                  Download .jsonl
                </button>
              </div>
              <pre className="wt-corpus-raw">
                {truncated
                  ? raw.slice(0, PREVIEW_CHARS) +
                    "\n\n… truncated for display — download for the full transcript."
                  : raw}
              </pre>
            </>
          )}
        </div>
      )}
    </li>
  );
}
